import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export interface InternalConversation {
  id: string;
  created_at: string;
  updated_at: string;
  state: "open" | "completed";
  summary: string | null;
  first_user_message: string;
}

export function useInternalConversations(agentId?: string) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<InternalConversation[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInternalConversations = async () => {
    if (!user?.id || !agentId) {
      setIsLoading(false);
      return;
    }

    try {
      console.log(
        `[INTERNAL CONVS] Fetching for user_id: ${user.id}, agent_id: ${agentId}`,
      );

      // Fetch all internal conversations for this user and agent
      const { data: conversations, error: conversationError } = await supabase
        .from("conversations")
        .select(
          `
          id,
          created_at,
          updated_at,
          state,
          summary,
          messages(
            id,
            content,
            sender_type,
            created_at
          )
        `,
        )
        .eq("agent_id", agentId)
        .eq("user_id", user.id)
        .is("agent_chat_user_id", null) // Ensure it's an internal conversation
        .order("updated_at", { ascending: false });

      if (conversationError) {
        throw conversationError;
      }

      if (!conversations) {
        console.log(`[INTERNAL CONVS] No conversations found`);
        setConversations([]);
        return;
      }

      console.log(`[INTERNAL CONVS] Raw conversations from DB:`, conversations);

      // Process conversations to get first user message for preview
      const processedConversations: InternalConversation[] = conversations.map(
        (conv) => {
          const messages = conv.messages || [];

          // Find first user message for preview
          const firstUserMessage = messages.find(
            (msg: any) => msg.sender_type === "user",
          );

          return {
            id: conv.id,
            created_at: conv.created_at,
            updated_at: conv.updated_at,
            state: conv.state as "open" | "completed",
            summary: conv.summary,
            first_user_message: firstUserMessage?.content || "New conversation",
          };
        },
      );

      console.log(
        `[INTERNAL CONVS] Found ${processedConversations.length} conversations`,
      );
      console.log(
        `[INTERNAL CONVS] Processed conversations:`,
        processedConversations,
      );
      setConversations(processedConversations);
    } catch (error) {
      console.error("[INTERNAL CONVS] Error fetching conversations:", error);
      setError("Failed to load conversations");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInternalConversations();
  }, [user?.id, agentId]);

  return {
    conversations,
    isLoading,
    error,
    refetch: fetchInternalConversations,
  };
}
